import {CheckCircleTwoTone} from '@ant-design/icons';
import {Button, Col, Modal, Row, Space} from 'antd';
import {FilterValue} from 'antd/es/table/interface';
import saveAs from 'file-saver';
import JSZip from 'jszip';
import {FC, useState} from 'react';
import {Link} from 'react-router-dom';
import {TranslateFn, useTranslation} from '../../translation';
import {AppTitle} from '../../util/AppTitle';
import {DEFAULT_API_NAME, useApi} from '../../util/Auth';
import {TimestampToDayjsDate} from '../../util/date';
import {ApartmentCodeSelect} from '../input/ApartmentCodeSelect';
import {OwnerSelect} from '../input/OwnerSelect';
import {SeasonSelect} from '../input/SeasonSelect';
import {ContentDiv} from '../layout/ContentDiv';
import {useStateFn} from '../useStateFn';
import {ArticleType, ArticleTypeLabel} from './Article';
import {OrderRecord, renderOrderNumber} from './Order';
import {
  booleanFilters,
  getDefaultSorter,
  getFiltersForAPI,
  TableColumnsProp,
  TableList,
} from './Table';

interface GoldCardRecord
  extends Pick<
    OrderRecord,
    | 'id'
    | 'createDate'
    | 'updateDate'
    | 'orderNumber'
    | 'season'
    | 'owner'
    | 'recipientName'
    | 'apartment'
  > {
  nbGoldCards: number;
  articleName: string;
}

export const getGoldCardColumns = (
  t: TranslateFn,
): TableColumnsProp<GoldCardRecord> => [
  {
    title: t('orders.createDate.short'),
    key: 'createDate',
    dataIndex: 'createDate',
    render: (createDate) => TimestampToDayjsDate(createDate).format('L'),
    sorter: true,
  },
  {
    title: t('orders.orderNumber'),
    key: 'orderNumber',
    dataIndex: 'orderNumber',
    searchInput: true,
    sorter: true,
    render: (_, order) => renderOrderNumber(order),
  },
  {
    title: t('orders.season'),
    key: 'seasonId',
    dataIndex: 'seasonId',
    sorter: true,
    render: (_, {season}: GoldCardRecord) => season?.code,
    searchInput: true,
    searchSelect: (props) => <SeasonSelect {...props} />,
  },
  {
    title: t('orders.owner'),
    key: 'ownerId',
    dataIndex: 'ownerId',
    sorter: true,
    render: (_, {owner}) =>
      owner ? (
        owner?.user ? (
          <Link to={`/admin/owner/${owner.id}`}>
            {owner?.user?.lastName} {owner?.user?.firstName}
          </Link>
        ) : (
          <Link to={`/admin/owner/${owner.id}`}>{owner.id}</Link>
        )
      ) : null,
    searchInput: true,
    searchSelect: (props) => <OwnerSelect {...props} />,
  },
  {
    title: t('orders.recipient'),
    key: 'recipientName',
    dataIndex: 'recipientName',
    sorter: true,
    render: (_, entity) => entity?.recipientName,
    searchInput: true,
  },
  {
    title: t('orders.apartment'),
    key: 'apartmentId',
    dataIndex: 'apartmentId',
    sorter: true,
    render: (_, {apartment}) => apartment?.code,
    searchInput: true,
    searchSelect: (props) => <ApartmentCodeSelect {...props} />,
  },
  {
    title: t('orders.nbGoldCards'),
    key: 'nbGoldCards',
    dataIndex: 'nbGoldCards',
    // sorter: true,
    render: (_, entity) => entity?.nbGoldCards,
    // searchInput: true,
  },
  // {
  //   title: t('orders.article'),
  //   key: 'articleId',
  //   dataIndex: 'articleId',
  //   sorter: true,
  //   render: (_, entity) => entity?.articleName,
  //   searchInput: true,
  // },
  {
    title: t('orders.articleType'),
    key: 'articleType',
    dataIndex: 'articleType',
    sorter: true,
    render: (articleType: ArticleType) => t(ArticleTypeLabel[articleType]),
    // searchInput: true,
    // searchSelect: (props) => (
    //   <Select<string>
    //     {...props}
    //     options={Object.entries(ArticleTypeLabel)
    //       .filter(([key, value]) =>
    //         [ArticleType.POOL_GOLD, ArticleType.POOL_GOLD_ADDITIONAL].includes(
    //           Number(key),
    //         ),
    //       )
    //       .map(([type, label]) => ({
    //         value: type,
    //         label: t(label),
    //       }))}
    //     showSearch
    //   />
    // ),
  },
  {
    title: t('orders.isRenewal.short'),
    key: 'isRenewal',
    dataIndex: 'isRenewal',
    render: (isRenewal) =>
      isRenewal ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : null,
    filters: booleanFilters(t),
  },
  {
    title: t('orders.renewalCardNo.short'),
    key: 'renewalCardNo',
    dataIndex: 'renewalCardNo',
    render: (renewalCardNo) => renewalCardNo,
    searchInput: true,
  },
];

const ExportButton: FC<{
  columns: TableColumnsProp<GoldCardRecord>;
  exportFilter: Record<string, FilterValue | null>;
}> = (props) => {
  const api = useApi();
  const {t} = useTranslation();

  const [loading, setLoading] = useState(false);

  const handleExportClick = async () => {
    if (!api) return;

    if (!props.exportFilter['seasonId']) {
      Modal.warning({
        title: 'Aucune saison sélectionnée',
        content: `Merci de rajouter un filtre sur la colonne saison avant d'exporter.`,
      });
      return;
    }

    setLoading(true);
    try {
      const {csv, photos} = (await api.get(
        DEFAULT_API_NAME,
        `/admin/gold-cards/export`,
        {
          queryStringParameters: {
            ...getFiltersForAPI(props.exportFilter, {}, props.columns),
          },
        },
      )) as {csv: string; photos: {[key: string]: string}};

      const fileName = `export-${new Date().toISOString().replace(/:/g, '-')}`;

      const zip = new JSZip();
      zip.file(`${fileName}.csv`, csv);

      /* const photosFolder = zip.folder('photos')!;
      for (const [id, url] of Object.entries(photos)) {
        const res = await fetch(url);
        if (!res.ok) continue;
        const photo = await res.arrayBuffer();

        let extension = '';
        switch (res.headers.get('Content-Type')) {
          case 'image/jpeg':
          case 'image/jpg':
            extension = '.jpg';
            break;
          case 'image/png':
            extension = '.png';
            break;
        }
        photosFolder.file([id, extension].join(''), photo);
      } */

      const content = await zip.generateAsync({type: 'blob'});
      saveAs(content, `${fileName}.zip`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      loading={loading}
      onClick={handleExportClick}
      type="primary"
      // style={{display: showExport ? '' : 'none'}}
    >
      {t('general.export')}
    </Button>
  );
};

export const AdminGoldCards: FC = () => {
  const {t} = useTranslation();

  // const [total, setTotal] = useState<number>();
  const [exportFilter, setExportFilter] = useState<
    Record<string, FilterValue | null>
  >({});

  const columns = useStateFn(() => getGoldCardColumns(t), [t]);
  const defaultSorter = useStateFn(
    () => getDefaultSorter(columns, 'orderNumber'),
    [columns],
  );

  return (
    <ContentDiv>
      <Row justify="space-between" align="middle">
        <Col>
          <AppTitle level={3} style={{marginTop: 0}}>
            {t('cardsGold.title')}
          </AppTitle>
        </Col>
        <Col style={{margin: 'auto 0'}}>
          <Space>
            <ExportButton columns={columns} exportFilter={exportFilter} />
          </Space>
        </Col>
      </Row>

      <TableList<GoldCardRecord>
        singularRoute={'#'}
        pluralRoute={'/admin/gold-cards'}
        columns={columns}
        // refreshDate={refreshDate}
        // setEditEntity={setEditEntity}
        defaultSorter={defaultSorter}
        hideActionColumn
        // onTotalChange={setTotal}
        onFilterChange={setExportFilter}
      />
    </ContentDiv>
  );
};
